<template>
  <div class="max-w-lg w-full mx-auto">
    <div class="mt-8 mx-auto max-w-7xl px-2 sm:mt-4">
      <div class="text-center">
        <h1 class="text-2xl tracking-tight font-extrabold text-gray-900 sm:text-2xl md:text-2xl whitespace-nowrap">
          <span class="block text-mygrad">Tell us who you are</span>
        </h1>
        <p class="mt-2 text-base text-gray-600">
          We’ll use this to deliver your snapshot(s).
        </p>
      </div>
    </div>
    <div v-if="noResultsSubmitted" class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-left mt-6">
      <div class="text-sm text-emerald-900">You're all set</div>
      <div class="mt-1 text-base font-semibold text-emerald-900">
        <span v-if="noResultsNotifyOptIn">We'll email you when we find photos of you.</span>
        <span v-else>Check back later for new photos.</span>
      </div>
      <div class="mt-2 text-sm text-emerald-900">
        You can also try another selfie if you'd like to improve the match.
      </div>
      <button
        type="button"
        class="mt-3 inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-semibold bg-white text-emerald-900 border border-emerald-300 hover:bg-emerald-100"
        @click="$emit('retake-selfie')"
      >
        Try another selfie
      </button>
    </div>
    <ValidationObserver v-else v-slot="{ handleSubmit }">
      <form class="space-y-4 text-gray-700 mt-6" @submit.prevent="handleSubmit(submit)">
        <div v-if="noResults && hasSelfie" class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-left">
          <div class="text-sm text-amber-900">No photos found yet</div>
          <div class="mt-1 text-base font-semibold text-amber-900">
            We couldn't find any photos from this selfie.
          </div>
          <div class="mt-2 text-sm text-amber-900">
            If you want alerts when new photos are added, confirm your details and consent below.
          </div>
          <button
            type="button"
            class="mt-3 inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-semibold bg-white text-amber-900 border border-amber-300 hover:bg-amber-100"
            @click="$emit('retake-selfie')"
          >
            Try another selfie
          </button>
        </div>
        <div v-else-if="foundCount" class="rounded-lg border border-gray-300 bg-white p-4 text-left">
          <div class="text-sm text-gray-600">Photos found</div>
          <div class="mt-1 text-2xl font-bold text-gray-900">
            {{ foundCount }} photo{{ foundCount === 1 ? '' : 's' }}
          </div>
          <div class="mt-2 text-sm text-gray-700">
            Enter your details to view them.
          </div>
        </div>
        <div class="w-full">
          <ValidationProvider rules="required|min:3" v-slot="{ errors, valid }">
            <label class="block mb-1" for="formGridCode_name">Full name</label>
            <input
              class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
              type="text"
              id="formGridCode_name"
              name="name"
              v-model="form.name"
              placeholder="Your name shown at your graduation"
              v-bind:class="{
                'border-red-700 text-red-900': errors.length > 0,
                'border-green-700': valid,
              }"
            />
          </ValidationProvider>
        </div>
        <div class="w-full">
          <ValidationProvider
            name="Email Address"
            rules="required|email|personal_email"
            v-slot="{ valid, errors }"
            vid="email"
          >
            <label class="block mb-1" for="formGridCode_email">Email Address</label>
            <input
              ref="email"
              class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
              type="email"
              id="formGridCode_email"
              name="email"
              v-model="form.email"
              placeholder="john.doe@example.com"
              v-bind:class="{
                'border-red-700 text-red-900': errors.length > 0,
                'border-green-700': valid,
              }"
            />
            <span>{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
        <div class="w-full">
          <ValidationProvider
            name="Confirmation Email Address"
            rules="required|confirmed:email"
            v-slot="{ valid, errors }"
          >
            <label class="block mb-1" for="formGridCode_email_conf">Confirm Email Address</label>
            <input
              class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
              type="email"
              id="formGridCode_email_conf"
              name="confirmed_email"
              v-model="form.confirmEmail"
              placeholder="john.doe@example.com"
              v-bind:class="{
                'border-red-700 text-red-900': errors.length > 0,
                'border-green-700': valid,
              }"
            />
            <span>{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
        <div v-if="rememberEnabled" class="w-full">
          <label class="flex items-start space-x-3 text-sm text-gray-700">
            <input
              type="checkbox"
              v-model="form.rememberMe"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span>Remember me on this device for this ceremony.</span>
          </label>
          <button
            v-if="remembered"
            type="button"
            class="mt-2 text-xs text-gray-500 underline"
            @click="$emit('forget-remembered')"
          >
            Forget this device
          </button>
        </div>
        <div v-if="saveSelfieEnabled" class="w-full">
          <label class="flex items-start space-x-3 text-sm text-gray-700">
            <input
              type="checkbox"
              v-model="form.saveSelfie"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span>
              I consent to {{ saveSelfieConsentOrgName || 'MyGraduationClip' }} creating a biometric template
              from my selfie to notify me when new photos are found for this ceremony.
              I can withdraw consent at any time via the unsubscribe link in our emails.
            </span>
          </label>
          <p class="mt-2 text-xs text-gray-500">
            By consenting, you agree to the
            <a :href="saveSelfieConsentPolicyUrl || '/privacy-policy'" class="underline text-blue-700" target="_blank" rel="noopener">
              privacy policy
            </a>.
            <span v-if="saveSelfieConsentContactEmail">
              Questions? Contact
              <a :href="`mailto:${saveSelfieConsentContactEmail}`" class="underline text-blue-700">
                {{ saveSelfieConsentContactEmail }}
              </a>.
            </span>
          </p>
        </div>
        <button
          type="submit"
          class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium w-full rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {{ noResults ? 'Continue' : 'View my photos' }}
        </button>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { extend } from "vee-validate";
import { required, min, max, email, regex, confirmed } from "vee-validate/dist/rules";

extend("personal_email", {
  message: (field) =>
    "The " +
    field +
    " value is not a personal email, please enter an email not ending in ac.uk.",
  validate: (value) => !/^.*((\.ac\.uk)|(bpp\.com))$/.test(value),
});

extend("required", {
  ...required,
  message: "This field is required",
});
extend("min", min);
extend("confirmed", {
  ...confirmed,
  message: "Your email addresses do not match",
});
extend("regex", regex);
extend("max", max);
extend("email", email);

export default {
  name: "SnapshotContactForm",
  props: {
    name: String,
    email: String,
    foundCount: { type: Number, default: 0 },
    noResults: { type: Boolean, default: false },
    noResultsSubmitted: { type: Boolean, default: false },
    noResultsNotifyOptIn: { type: Boolean, default: false },
    hasSelfie: { type: Boolean, default: false },
    remembered: { type: Boolean, default: false },
    rememberEnabled: { type: Boolean, default: false },
    saveSelfieEnabled: { type: Boolean, default: false },
    saveSelfieConsentOrgName: { type: String, default: "" },
    saveSelfieConsentPolicyUrl: { type: String, default: "" },
    saveSelfieConsentContactEmail: { type: String, default: "" },
  },
  data() {
    return {
      form: {
        name: "",
        email: null,
        confirmEmail: null,
        rememberMe: false,
        saveSelfie: false,
      },
    };
  },
  created() {
    this.form.name = this.name || "";
    this.form.email = this.email || null;
    this.form.confirmEmail = this.email || null;
    this.form.rememberMe = this.remembered;
    this.form.saveSelfie = false;
  },
  watch: {
    remembered(newVal) {
      this.form.rememberMe = Boolean(newVal);
    },
  },
  methods: {
    submit() {
      this.$emit("submit", this.form);
    },
  },
};
</script>
